import { Controller } from 'stimulus';

const TomSelect = require('../lib/tom_select');

// Shows/hides the sections of the notification-rule form that only apply to
// certain event kinds (job scope, date offsets, stuck days, member role),
// and upgrades the criteria/recipient multi-selects to TomSelect.
// Event metadata comes from data-event-metadata (JSON keyed by event_key);
// field-condition metadata (type, dropdown options, template coverage per
// field name) from data-field-metadata, and drives the value picker + hint.
export default class extends Controller {
  static targets = ['eventSelect', 'scopeSection', 'offsetsSection', 'stuckSection',
                    'memberRoleSection', 'criteriaSection', 'scopeKind', 'statusParamsSection',
                    'fieldName', 'fieldValueText', 'fieldValueSelect', 'fieldHint'];

  connect() {
    this.metadata = JSON.parse(this.element.dataset.eventMetadata || '{}');
    this.fieldMetadata = JSON.parse(this.element.dataset.fieldMetadata || '{}');
    this.initMultiSelects();
    this.refresh();
  }

  disconnect() {
    (this.tomSelectInstances || []).forEach((ts) => ts.destroy());
    this.tomSelectInstances = [];
  }

  initMultiSelects() {
    this.tomSelectInstances = [];
    this.element.querySelectorAll('[data-notification-rule-multi]').forEach((el) => {
      const ts = new TomSelect(el, {
        plugins: ['remove_button'],
        persist: false,
        maxItems: null,
        hideSelected: true,
        placeholder: el.dataset.placeholder,
        onItemAdd: function () {
          this.setTextboxValue('');
          this.refreshOptions(false);
        },
      });
      this.tomSelectInstances.push(ts);
    });
  }

  eventChanged() {
    this.refresh();
  }

  scopeKindChanged() {
    this.refresh();
  }

  fieldChanged() {
    this.refreshFieldCondition();
  }

  // A dropdown field offers its own values (select) instead of free text;
  // the hidden twin is disabled so only one input submits.
  refreshFieldCondition() {
    if (!this.hasFieldNameTarget) { return; }

    const meta = this.fieldMetadata[this.fieldNameTarget.value];
    const options = (meta && meta.options) || [];
    const pickValue = options.length > 0;

    if (this.hasFieldValueSelectTarget) {
      const select = this.fieldValueSelectTarget;
      const current = select.value || (this.hasFieldValueTextTarget ? this.fieldValueTextTarget.value : '');
      select.innerHTML = '';
      select.appendChild(new Option('Pick a value', ''));
      options.forEach((option) => select.appendChild(new Option(option, option, false, option === current)));
      select.classList.toggle('hidden', !pickValue);
      select.disabled = !pickValue;
    }

    if (this.hasFieldValueTextTarget) {
      this.fieldValueTextTarget.classList.toggle('hidden', pickValue);
      this.fieldValueTextTarget.disabled = pickValue;
    }

    if (this.hasFieldHintTarget) {
      this.fieldHintTarget.textContent = this.fieldHint(meta);
    }
  }

  // Mirrors NotificationRulesHelper#notification_rule_field_hint.
  fieldHint(meta) {
    if (!meta) { return ''; }
    if (meta.column) { return 'Job column, on every job.'; }

    const kind = meta.options && meta.options.length ? `Job field, ${meta.options.join(' / ')}` : 'Job field';
    return `${kind}. On ${meta.with} of ${meta.total} templates.`;
  }

  refresh() {
    const meta = this.currentMetadata();

    this.toggle(this.scopeSectionTargets, meta.job_scoped);
    this.toggle(this.offsetsSectionTargets, meta.kind === 'date');
    this.toggle(this.stuckSectionTargets, meta.kind === 'stuck');
    this.toggle(this.memberRoleSectionTargets, this.currentEventKey() === 'added_to_job');
    this.toggle(this.statusParamsSectionTargets, this.currentEventKey() === 'job_status_changed');
    this.toggle(this.criteriaSectionTargets, meta.job_scoped && this.currentScopeKind() === 'criteria');
  }

  currentEventKey() {
    if (!this.hasEventSelectTarget) { return this.element.dataset.eventKey; }

    return this.eventSelectTarget.value;
  }

  currentMetadata() {
    return this.metadata[this.currentEventKey()] || {};
  }

  currentScopeKind() {
    const checked = this.scopeKindTargets.find((input) => input.checked);
    return checked ? checked.value : 'involved';
  }

  toggle(elements, show) {
    elements.forEach((el) => el.classList.toggle('hidden', !show));
  }
}
